// src/pages/public/BrowsePage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Seo from "../../components/public/Seo";

import { addToEdit, removeFromEdit, isInEdit, getEditItems } from "../../state/editStore";
import { getActiveEditId, setActiveEditId } from "../../state/activeEditStore";
import { listEdits, createEditMeta } from "../../state/editsStore";

import { rankProductsForQueryAsync } from "../../reco/semanticRanker";
import { EDIT_CHANGED_EVENT } from "../../state/editEvents";
import { getPublicProducts } from "../../lib/catalogApi";
import { useCountry } from "../../hooks/useCountry";
import {
  logProductSaved,
  logProductUnsaved,
  logProductAddedToBag,
  logProductRemovedFromBag,
} from "../../lib/interactionEventsApi";
import {
  addToFittingQueue,
  removeFromFittingQueue,
  isInFittingQueue,
  FITTING_QUEUE_CHANGED_EVENT,
} from "../../state/fittingRoomQueueStore";
import { normalizeBackendProduct } from "../../catalog/meshResolver";
import { isTryOnAvailable } from "../../catalog/tryOnAvailability";
import {
  addToBag,
  removeFromBag,
  isInBag,
  BAG_CHANGED_EVENT,
} from "../../state/bagStore";
import { fetchEditSignals } from "../../lib/editApi";
import { optimizedImageUrl } from "../../lib/optimizedImageUrl";

/**
 * NOTE:
 * This is a “public browse page” version of BrowsePanel.
 * It does NOT open panels. It behaves like a normal store page.
 * The "Go to fitting room" button sends users to "/?browse=1..."
 */


const CATEGORY_OPTIONS = [
  { id: "top", label: "Tops" },
  { id: "bottom", label: "Bottoms" },
  { id: "dress", label: "Dresses" },
  { id: "outer", label: "Outerwear" },
  { id: "shoe", label: "Shoes" },
  { id: "bag", label: "Bags" },
  { id: "hat", label: "Hats" },
  { id: "jewellery", label: "Jewellery" },
  { id: "accessory", label: "Accessories" },
];

const COLOUR_OPTIONS = [
  "Black",
  "White",
  "Cream",
  "Beige",
  "Brown",
  "Grey",
  "Blue",
  "Navy",
  "Green",
  "Pink",
  "Red",
  "Burgundy",
];

function CartIcon({ active }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" style={{ display: "block" }}>
      <path
        d="M4 6h2l1.2 6.2a1 1 0 0 0 1 .8h7.9a1 1 0 0 0 1-.76L18.4 8H7.1"
        fill="none"
        stroke={active ? "var(--cream)" : "var(--burgundy)"}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx="10"
        cy="18"
        r="1.4"
        fill={active ? "var(--cream)" : "var(--burgundy)"}
      />
      <circle
        cx="17"
        cy="18"
        r="1.4"
        fill={active ? "var(--cream)" : "var(--burgundy)"}
      />
    </svg>
  );
}

function ProductImageCarousel({ item }) {
  const images = useMemo(() => {
    const arr = [];

    if (Array.isArray(item.images)) arr.push(...item.images);
    if (item.image) arr.push(item.image);
    if (item.backImage) arr.push(item.backImage);
    if (item.imageBack) arr.push(item.imageBack);
    if (item.back) arr.push(item.back);

    return Array.from(new Set(arr.filter(Boolean)));
  }, [item]);

  const [index, setIndex] = useState(0);

  useEffect(() => setIndex(0), [images.length]);

  const hasMany = images.length > 1;

  function next(e) {
    e.stopPropagation();
    setIndex((i) => (i + 1) % images.length);
  }

  function prev(e) {
    e.stopPropagation();
    setIndex((i) => (i - 1 + images.length) % images.length);
  }

  return (
    <div style={{ position: "relative" }}>
<img
  src={optimizedImageUrl(images[index], {
    width: window.innerWidth <= 768 ? 420 : 600,
    quality: 78,
  })}
  alt={item.name}
  loading="lazy"
  decoding="async"
  style={{
    width: "100%",
    height: window.innerWidth <= 768 ? 155 : 220,
    objectFit: "cover",
    display: "block",
  }}
/>

      {hasMany && (
        <>
          <button
            type="button"
            onClick={prev}
            className="carousel-arrow left"
            aria-label="Previous image"
          >
            ‹
          </button>

          <button
            type="button"
            onClick={next}
            className="carousel-arrow right"
            aria-label="Next image"
          >
            ›
          </button>
        </>
      )}
    </div>
  );
}

function getColourHaystack(product) {
  return [
    product?.name || "",
    ...(Array.isArray(product?.styleTags) ? product.styleTags : []),
    product?.colour || "",
    product?.color || "",
  ]
    .join(" ")
    .toLowerCase();
}

function normalizeSignalText(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function buildProductSignalHaystack(product) {
  return normalizeSignalText(
    [
      product?.name,
      product?.title,
      product?.brand,
      product?.category,
      product?.variant,
      product?.fit,
      product?.budgetTier,
      product?.color,
      product?.colour,
      product?.material,
      product?.description,
      ...(Array.isArray(product?.styleTags) ? product.styleTags : []),
    ].join(" ")
  );
}

function scoreProductForEditSignals(product, editSignals) {
  if (!editSignals) return 0;

  const hay = buildProductSignalHaystack(product);

  const dominantColors = Array.isArray(editSignals?.colors?.dominant)
    ? editSignals.colors.dominant
    : [];

  const accentColors = Array.isArray(editSignals?.colors?.accents)
    ? editSignals.colors.accents
    : [];

  const allColors = Array.isArray(editSignals?.colors?.all)
    ? editSignals.colors.all
    : [];

  const silhouettes = Array.isArray(editSignals?.silhouettes)
    ? editSignals.silhouettes
    : [];

  const styles = Array.isArray(editSignals?.styles)
    ? editSignals.styles
    : [];

  const categories = Array.isArray(editSignals?.categories)
    ? editSignals.categories
    : [];

  const materials = Array.isArray(editSignals?.materials)
    ? editSignals.materials
    : [];

  const intents = Array.isArray(editSignals?.intents)
    ? editSignals.intents
    : [];

  const phrases = Array.isArray(editSignals?.phrases)
    ? editSignals.phrases
    : [];

  let score = 0;

  for (const value of categories) {
    const term = normalizeSignalText(value);
    if (term && hay.includes(term)) score += 7;
  }

  for (const value of styles) {
    const term = normalizeSignalText(value);
    if (term && hay.includes(term)) score += 5;
  }

  for (const value of intents) {
    const term = normalizeSignalText(value);
    if (term && hay.includes(term)) score += 5;
  }

  for (const value of silhouettes) {
    const term = normalizeSignalText(value);
    if (term && hay.includes(term)) score += 4;
  }

  for (const value of materials) {
    const term = normalizeSignalText(value);
    if (term && hay.includes(term)) score += 4;
  }

  for (const value of dominantColors) {
    const term = normalizeSignalText(value);
    if (term && hay.includes(term)) score += 3;
  }

  for (const value of accentColors) {
    const term = normalizeSignalText(value);
    if (term && hay.includes(term)) score += 2;
  }

  for (const value of allColors) {
    const term = normalizeSignalText(value);
    if (term && hay.includes(term)) score += 1;
  }

  for (const value of phrases) {
    const term = normalizeSignalText(value);
    if (term && hay.includes(term)) score += 2;
  }

  return score;
}

function normalizeProductImages(product) {
  const gallery = Array.isArray(product?.images)
    ? product.images.map((img) => (typeof img === "string" ? img : img?.url)).filter(Boolean)
    : [];

  return Array.from(
    new Set([product?.image_url, ...gallery].filter(Boolean))
  );
}

function buildBrowseProduct(rawProduct) {
  const tryOnProduct = normalizeBackendProduct(rawProduct);
  const images = normalizeProductImages(rawProduct);

  return {
    ...tryOnProduct,

    rawProduct,

    title: rawProduct?.title || tryOnProduct.name,
    name: rawProduct?.title || tryOnProduct.name,
    description: rawProduct?.description || "",
    brand: rawProduct?.brand || tryOnProduct.brand || "Unknown Store",

    color: rawProduct?.color || null,
    material: rawProduct?.material || null,

    price_amount: rawProduct?.price_amount || null,
    price: Number(rawProduct?.price_amount || 0),
    currency: rawProduct?.currency || "NZD",

    productUrl: rawProduct?.product_url || tryOnProduct.productUrl || null,
    image: tryOnProduct.image || images[0] || null,
    images,

    variants: rawProduct?.variants || [],

    tryOn: tryOnProduct,
    tryOnAvailable: isTryOnAvailable(tryOnProduct),
  };
}


export default function BrowsePage() {
  const navigate = useNavigate();
  const [mobileControlsOpen, setMobileControlsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [categories, setCategories] = useState({});

  const [sortMode, setSortMode] = useState("default");
  const [useEditRanking, setUseEditRanking] = useState(true);
  const [editSignals, setEditSignals] = useState(null);
  const [editSignalsLoading, setEditSignalsLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedColours, setSelectedColours] = useState([]);

  const [query, setQuery] = useState("");
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [recoLoading, setRecoLoading] = useState(false);

  const [catalogProducts, setCatalogProducts] = useState([]);
  const [countryTick, setCountryTick] = useState(0);
  const { country } = useCountry();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(false);

  const [editVersion, setEditVersion] = useState(0);
  const bumpEditVersion = () => setEditVersion((v) => v + 1);

  const [queueTick, setQueueTick] = useState(0);
  const [bagTick, setBagTick] = useState(0);

  useEffect(() => {
    const onQueueChanged = () => setQueueTick((t) => t + 1);
    window.addEventListener(FITTING_QUEUE_CHANGED_EVENT, onQueueChanged);
    return () => window.removeEventListener(FITTING_QUEUE_CHANGED_EVENT, onQueueChanged);
  }, []);

  useEffect(() => {
    const onBagChanged = () => setBagTick((t) => t + 1);
    window.addEventListener(BAG_CHANGED_EVENT, onBagChanged);
    return () => window.removeEventListener(BAG_CHANGED_EVENT, onBagChanged);
  }, []);

  const [activeEditId, setActiveEditIdState] = useState(getActiveEditId() || "");
  const [allEdits, setAllEdits] = useState([]);

  const stores = useMemo(() => {
    const map = new Map();

    for (const product of catalogProducts) {
      const brand = (product.brand || "").trim();
      if (!brand) continue;

      const id = brand.toLowerCase().replace(/\s+/g, "-");

      if (!map.has(id)) {
        map.set(id, {
          id,
          name: brand,
          location: country,
        });
      }
    }

    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [catalogProducts]);

  useEffect(() => {
    const onEditChanged = () => {
      setActiveEditIdState(getActiveEditId() || "");
      bumpEditVersion();
    };
    window.addEventListener(EDIT_CHANGED_EVENT, onEditChanged);
    return () => window.removeEventListener(EDIT_CHANGED_EVENT, onEditChanged);
  }, []);

  useEffect(() => {
  const onCountryChanged = () => setCountryTick((t) => t + 1);
  window.addEventListener("veya_country_changed", onCountryChanged);
  return () => window.removeEventListener("veya_country_changed", onCountryChanged);
}, []);

  useEffect(() => {
    setAllEdits(listEdits() || []);
  }, [editVersion]);

  const baseProducts = useMemo(() => {
    let list = [...catalogProducts];

    if (selectedStoreId) {
      const selectedStore = stores.find((s) => s.id === selectedStoreId);
      const selectedName = selectedStore?.name || null;
      if (selectedName) {
        list = list.filter((p) => p.brand === selectedName);
      }
    }

    if (Object.keys(categories).length) {
  list = list.filter((p) => categories[p.category] !== false);
}

    if (selectedColours.length) {
      list = list.filter((p) => {
        const hay = getColourHaystack(p);
        return selectedColours.some((colour) => hay.includes(colour.toLowerCase()));
      });
    }

    return list;
  }, [catalogProducts, selectedStoreId, stores, categories, selectedColours]);

  const editItems = useMemo(() => getEditItems(activeEditId), [activeEditId, editVersion]);

  useEffect(() => {
  let cancelled = false;

  async function loadEditSignals() {
    if (!activeEditId || !useEditRanking) {
      setEditSignals(null);
      return;
    }

    setEditSignalsLoading(true);

    try {
      const data = await fetchEditSignals(activeEditId);
      if (!cancelled) {
        setEditSignals(data || null);
      }
    } catch (e) {
      console.error("[browse page] failed to load edit signals", e);
      if (!cancelled) {
        setEditSignals(null);
      }
    } finally {
      if (!cancelled) {
        setEditSignalsLoading(false);
      }
    }
  }

  loadEditSignals();

  return () => {
    cancelled = true;
  };
}, [activeEditId, useEditRanking, editVersion]);


  useEffect(() => {
    let cancelled = false;

    async function loadCatalogProducts() {
      if (!country) return;
      setCatalogLoading(true);
      try {
        const rows = await getPublicProducts(country);
        if (cancelled) return;

const products = Array.isArray(rows) ? rows : rows?.products || [];

const normalized = products.map(buildBrowseProduct);

        setCatalogProducts(normalized);
      } catch (e) {
        console.error("[browse page] failed to load public products", e);
        if (!cancelled) setCatalogProducts([]);
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    }

    loadCatalogProducts();

    return () => {
      cancelled = true;
    };
}, [country, countryTick]);

useEffect(() => {
  let cancelled = false;
  const hasQuery = !!query.trim();

  setRecoLoading(false);
  setRecommendedProducts([]);

  async function run() {
    const shouldRankByEdit = useEditRanking && !!activeEditId;

    if (!hasQuery && !shouldRankByEdit) {
      return;
    }

    // query still uses semantic ranker
    if (hasQuery) {
      setRecoLoading(true);
      try {
        const sorted = await rankProductsForQueryAsync(
          baseProducts,
          editItems,
          activeEditId,
          query,
          { useEditRanking: false }
        );

        if (!cancelled) setRecommendedProducts(sorted);
      } catch (e) {
        console.error("[browse page] ranking failed", e);
        if (!cancelled) setRecommendedProducts(baseProducts);
      } finally {
        if (!cancelled) setRecoLoading(false);
      }
      return;
    }

    // edit-only ranking now uses stored backend signals
 if (shouldRankByEdit) {
  if (!editSignals) {
    return;
  }

  const scored = [...baseProducts].map((product, index) => ({
    product,
    index,
    score: scoreProductForEditSignals(product, editSignals),
  }));

  const hasAnySignalMatch = scored.some((row) => row.score > 0);

  if (!hasAnySignalMatch) {
    if (!cancelled) setRecommendedProducts([]);
    return;
  }

  const sorted = scored
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.index - b.index;
    })
    .map((row) => row.product);

  if (!cancelled) {
    setRecommendedProducts(sorted);
  }
}
  }

  run();

  return () => {
    cancelled = true;
  };
}, [query, baseProducts, editItems, activeEditId, useEditRanking, editSignals]);

  const productsToShow = useMemo(() => {
    let list = recommendedProducts.length
      ? [...recommendedProducts]
      : [...baseProducts];

    switch (sortMode) {
      case "az":
        list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        break;

      case "za":
        list.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
        break;

      case "price_low":
        list.sort((a, b) => (a.price || 0) - (b.price || 0));
        break;

      case "price_high":
        list.sort((a, b) => (b.price || 0) - (a.price || 0));
        break;

      default:
        break;
    }

    return list;
  }, [recommendedProducts, baseProducts, sortMode]);

  function ensureActiveEdit() {
    let id = getActiveEditId();
    if (!id) {
      const meta = createEditMeta({ name: "My Edit" });
      id = meta.id;
      setActiveEditId(id);
    }
    setActiveEditIdState(id);
    bumpEditVersion();
    return id;
  }

  function toggleColour(colour) {
    setSelectedColours((prev) =>
      prev.includes(colour)
        ? prev.filter((c) => c !== colour)
        : [...prev, colour]
    );
  }

  return (
    <>
      <Seo
        title="Browse — Veya"
        description="Browse the catalogue like a traditional store. Save pieces into your edits, then open the fitting room when you're ready."
      />

      <div className="public-breadcrumb">
        <Link to="/edits" className="public-navlink">
          My Edits
        </Link>
        <span className="public-muted"> / Browse</span>
      </div>

      <h1 className="public-h1" style={{ marginBottom: 6 }}>
        Browse
      </h1>
      <p className="public-lede" style={{ marginTop: 0 }}>
        Save items to an edit to refine your search. Use the coat hanger icon to add to the fitting room for try on or add to the cart to purchase from the store.
      </p>


    {isMobile && (
      <button
        type="button"
        onClick={() => setMobileControlsOpen((v) => !v)}
        className="mobile-browse-controls-btn"
      >
        Filter & Search {mobileControlsOpen ? "×" : "▾"}
      </button>
    )}

  <div
    className="public-card"
    style={{
      display:
        isMobile && !mobileControlsOpen
          ? "none"
          : undefined,
    padding: 14,
    marginBottom: 14,
    width: "100%",
    maxWidth: "100%",
    overflow: "hidden", // 👈 critical
  }}
>
        <div
  style={{
    display: "grid",
    minWidth: 0, // 👈 critical
    gridTemplateColumns: isMobile ? "1fr 1fr" : "minmax(180px, 220px) minmax(240px, 1fr) minmax(260px, 1.3fr) minmax(180px, 220px) minmax(160px, 180px)",
    gap: isMobile ? 10 : 12,
            alignItems: "end",
          }}
        >
          <div>
            <div style={{ fontSize: 12, opacity: 0.72, marginBottom: 6 }}>Store</div>
            <select
              value={selectedStoreId || ""}
              onChange={(e) => setSelectedStoreId(e.target.value)}
              style={{
                width: "100%",
                borderRadius: 12,
                padding: "10px 10px",
                border: "1px solid rgba(43,43,43,0.12)",
                background: "rgba(251,247,242,0.70)",
              }}
            >
              <option value="">All stores</option>
              {stores.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div style={{ fontSize: 12, opacity: 0.72, marginBottom: 6 }}>Edit</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", minWidth: 0 }}>
              <select
                value={activeEditId || ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setActiveEditId(v);
                  setActiveEditIdState(v);
                  bumpEditVersion();
                }}
                style={{
                  flex: 1,
                  borderRadius: 12,
                  padding: "10px 10px",
                  border: "1px solid rgba(43,43,43,0.12)",
                  background: "rgba(251,247,242,0.70)",
                }}
              >
                {allEdits.length ? (
                  allEdits.map((ed) => (
                    <option key={ed.id} value={ed.id}>
                      {(ed.name || "").trim() || "Untitled Edit"}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>
                    No edits yet
                  </option>
                )}
              </select>

              <label style={{ display: "flex", gap: 4, alignItems: "center", fontSize: 11, whiteSpace: "nowrap", flexShrink: 0 }}>
                <input
                  type="checkbox"
                  checked={useEditRanking}
                  onChange={(e) => setUseEditRanking(e.target.checked)}
                />
                Use Edit
              </label>
            </div>
          </div>

<div style={{ gridColumn: isMobile ? "1 / -1" : "auto" }}>
  <div style={{ fontSize: 12, opacity: 0.72, marginBottom: 6 }}>Search</div>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="e.g. trench, burgundy, minimal, wide leg…"
                style={{
                  flex: 1,
                  borderRadius: 12,
                  padding: "10px 10px",
                  border: "1px solid rgba(43,43,43,0.12)",
                  background: "rgba(251,247,242,0.70)",
                }}
              />
              {!!query.trim() && (
                <button className="public-btn" type="button" onClick={() => setQuery("")}>
                  Clear
                </button>
              )}
            </div>
            {recoLoading ? (
              <div className="public-muted" style={{ fontSize: 12, marginTop: 6 }}>
                Searching…
              </div>
            ) : null}
          </div>

          <div>
            <div style={{ fontSize: 12, opacity: 0.72, marginBottom: 6 }}>Sort</div>
            <select
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value)}
              style={{
                width: "100%",
                borderRadius: 12,
                padding: "10px 10px",
                border: "1px solid rgba(43,43,43,0.12)",
                background: "rgba(251,247,242,0.70)",
              }}
            >
              <option value="default">Default</option>
              <option value="az">A → Z</option>
              <option value="za">Z → A</option>
              <option value="price_low">Price low → high</option>
              <option value="price_high">Price high → low</option>
            </select>
          </div>

          <div style={{ position: "relative" }}>
            <div style={{ fontSize: 12, opacity: 0.72, marginBottom: 6 }}>Filters</div>
            <button
              type="button"
              onClick={() => setFiltersOpen((v) => !v)}
              style={{
                width: "100%",
                borderRadius: 12,
                padding: "10px 10px",
                border: "1px solid rgba(43,43,43,0.12)",
                background: "rgba(251,247,242,0.70)",
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              Filters ▾
            </button>
          </div>
        </div>

        <div style={{ height: 1, background: "rgba(43,43,43,0.08)", margin: "14px 0" }} />

        {filtersOpen && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: 20,
              padding: 14,
              borderRadius: 14,
              background: "rgba(251,247,242,0.72)",
              border: "1px solid rgba(43,43,43,0.08)",
            }}
          >
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>
                Store
              </div>
              <label style={{ display: "block", marginBottom: 6 }}>
                <input
                  type="checkbox"
                  checked={!selectedStoreId}
                  onChange={() => setSelectedStoreId("")}
                />{" "}
                All stores
              </label>
              {stores.map((s) => (
                <label key={s.id} style={{ display: "block", marginBottom: 6 }}>
                  <input
                    type="checkbox"
                    checked={selectedStoreId === s.id}
                    onChange={() => setSelectedStoreId(s.id)}
                  />{" "}
                  {s.name}
                </label>
              ))}
            </div>


            <div>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>
                Category
              </div>
              {CATEGORY_OPTIONS.map((c) => (
                <label key={c.id} style={{ display: "block", marginBottom: 6 }}>
                  <input
                    type="checkbox"
                    checked={categories[c.id] !== false}
                    onChange={(e) =>
                      setCategories((prev) => ({
                        ...prev,
                        [c.id]: e.target.checked,
                      }))
                    }
                  />{" "}
                  {c.label}
                </label>
              ))}
            </div>

            <div>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>
                Colour
              </div>
              {COLOUR_OPTIONS.map((colour) => (
                <label key={colour} style={{ display: "block", marginBottom: 6 }}>
                  <input
                    type="checkbox"
                    checked={selectedColours.includes(colour)}
                    onChange={() => toggleColour(colour)}
                  />{" "}
                  {colour}
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      <section className="public-section" style={{ marginTop: 0 }}>
        {catalogLoading ? (
          <div className="public-muted" style={{ marginBottom: 12 }}>
            Loading products…
          </div>
        ) : null}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "repeat(2, minmax(0, 1fr))"
              : "repeat(auto-fill, minmax(180px, 1fr))",
            gap: isMobile ? 10 : 14,
          }}
        >
          {productsToShow.map((item) => {
            const inEdit = isInEdit(item.id, activeEditId);
            const queued = isInFittingQueue(item.id);
            const inBag = isInBag(item.id);
            const tryOnAvailable = item.tryOnAvailable;
            void bagTick;

           return (
  <div
    key={item.id}
    className="product-card"
    onClick={() => navigate(`/products/${item.id}`)}
    role="button"
    tabIndex={0}
    style={{
                  borderRadius: 18,
                  overflow: "hidden",
                  background: "rgba(255,255,255,0.22)",
                  border: "1px solid rgba(255,255,255,0.55)",
                  backdropFilter: "blur(18px) saturate(1.12)",
                  WebkitBackdropFilter: "blur(18px) saturate(1.12)",
                  boxShadow:
                    "inset 0 1px 0 rgba(255,255,255,0.55), 0 18px 44px rgba(0,0,0,0.08)",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div style={{ position: "relative" }}>
                  <ProductImageCarousel item={item} />

                  <button
                    type="button"
                    className={`bp-save-btn bp-save-wrap ${inBag ? "active" : ""}`}
                    aria-label="Add to Bag"
                    onClick={(e) => {
                      e.stopPropagation();

                      if (inBag) {
                        removeFromBag(item.id);

                        logProductRemovedFromBag({
                          productId: item.id,
                          editId: activeEditId || null,
                          surface: "browse_page",
                          category: item.category,
                          variant: item.variant || null,
                        });
                      } else {
                        addToBag(item);

                        logProductAddedToBag({
                          productId: item.id,
                          editId: activeEditId || null,
                          surface: "browse_page",
                          category: item.category,
                          variant: item.variant || null,
                        });
                      }

                      setBagTick((t) => t + 1);
                    }}
                  >
                    <CartIcon active={inBag} />
                    <span className="veya-tooltip bp-save-tooltip">
                      {inBag ? "Remove from Bag" : "Add to Bag"}
                    </span>
                  </button>

                  {tryOnAvailable && (
                    <button
                      type="button"
                      className={`bp-save-btn bp-save-wrap bp-fitting-btn ${queued ? "active" : ""}`}
                      aria-label="Add to Fitting Room"
                      onClick={(e) => {
                        e.stopPropagation();

                        if (queued) removeFromFittingQueue(item.id);
                        else addToFittingQueue(item);

                        setQueueTick((t) => t + 1);
                      }}
                    >
                      <svg width="22" height="22" viewBox="0 0 24 24">
                        <path
                          d="M12 6a2 2 0 1 0-2-2"
                          fill="none"
                          stroke={queued ? "var(--cream)" : "var(--burgundy)"}
                          strokeWidth="1.8"
                          strokeLinecap="round"
                        />
                        <path
                          d="M4 12l8-4 8 4"
                          fill="none"
                          stroke={queued ? "var(--cream)" : "var(--burgundy)"}
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M4 12h16"
                          fill="none"
                          stroke={queued ? "var(--cream)" : "var(--burgundy)"}
                          strokeWidth="1.8"
                          strokeLinecap="round"
                        />
                      </svg>

                      <span className="veya-tooltip bp-save-tooltip">
                        Add to Fitting Room
                      </span>
                    </button>
                  )}

                  <div
                    style={{
                      position: "absolute",
                      bottom: 10,
                      left: 12,
                      fontSize: 11,
                      fontWeight: 700,
                      background: "rgba(251,247,242,0.85)",
                      padding: "4px 8px",
                      borderRadius: 999,
                      border: "1px solid rgba(43,43,43,0.08)",
                    }}
                  >
                    {item.brand}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={(e) => {
  e.stopPropagation();
                    let effectiveEditId = activeEditId;
                    if (!effectiveEditId) effectiveEditId = ensureActiveEdit();

                    if (inEdit) {
                      removeFromEdit(item.id, effectiveEditId);

                      logProductUnsaved({
                        productId: item.id,
                        editId: effectiveEditId,
                        surface: "browse_page",
                        category: item.category,
                        variant: item.variant || null,
                      });
                    } else {
                      addToEdit(
                        {
                          kind: "pinned_product",
                          productId: item.id,
                          title: item.name,
                          imageUrl: item.image,
                          sourceUrl: item.productUrl || null,
                          createdAt: Date.now(),
                          category: item.category,
                          variant: item.variant || null,
                          styleTags: item.styleTags || [],
                          fit: item.fit || null,
                          budgetTier: item.budgetTier || null,
                        },
                        effectiveEditId
                      );

                      logProductSaved({
                        productId: item.id,
                        editId: effectiveEditId,
                        surface: "browse_page",
                        category: item.category,
                        variant: item.variant || null,
                      });
                    }

                    bumpEditVersion();
                  }}
                  style={{
                    width: "100%",
                    padding: isMobile ? "9px 8px" : "12px 14px",
                    border: "none",
                    background: "rgba(251,247,242,0.95)",
                    borderTop: "1px solid rgba(43,43,43,0.08)",
                    fontWeight: 700,
                    fontSize: isMobile ? 12 : 13,
                    cursor: "pointer",
                  }}
                >
                  {inEdit ? "Saved" : "Save to Edit"}
                </button>
              </div>
            );
          })}
        </div>

        {!productsToShow.length ? (
          <p className="public-muted" style={{ marginTop: 14 }}>
            No products match your filters.
          </p>
        ) : null}
            </section>
                </>
  );
}